// Cache bust: v24 - Sentry integration
import * as React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/tiptap/_variables.scss";
import "./styles/tiptap/_keyframe-animations.scss";
import { initializeClientShell } from "./lib/clientShellRecovery";
import { AppRevealProvider } from "./contexts/AppRevealContext";
import { readBootMinVisibleMs } from "./lib/bootTiming";
import { shouldBootstrapAdminApp } from "./lib/devSurfaceRouting";

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const maybeRedirectPasswordResetVlink = () => {
  if (typeof window === "undefined") return;

  const { pathname, hash } = window.location;
  if (pathname !== "/reset-password") return;

  const vlinkMatch = hash.match(/[#&]vlink=(.+)/);
  if (!vlinkMatch) return;

  try {
    window.location.replace(decodeURIComponent(vlinkMatch[1]));
  } catch {
    // ResetPassword handles invalid links in-app.
  }
};

maybeRedirectPasswordResetVlink();

if (import.meta.env.DEV && typeof window !== "undefined") {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
    import.meta.hot?.on("vite:beforeFullReload", () => false);
  }
}

type AppModule = {
  default: React.ComponentType;
};

const logoStageMs = readBootMinVisibleMs();

function shouldLoadAdminApp(): boolean {
  if (typeof window === "undefined") {
    return import.meta.env.PROD && import.meta.env.VITE_APP_SURFACE === "admin";
  }

  return shouldBootstrapAdminApp(window.location.pathname, window.location.search);
}

// eslint-disable-next-line react-refresh/only-export-components
function BootstrapHost() {
  const [AppComponent, setAppComponent] = React.useState<React.ComponentType | null>(null);
  const [shellReady, setShellReady] = React.useState(false);
  const [minimumBootElapsed, setMinimumBootElapsed] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    let bootTimerId: number | null = window.setTimeout(() => {
      if (active) {
        setMinimumBootElapsed(true);
      }
    }, logoStageMs);

    const shellReadyPromise = initializeClientShell().then((shouldRender) => {
      if (!active || !shouldRender) {
        return false;
      }

      setShellReady(true);
      return true;
    });

    const appModulePromise: Promise<AppModule> = shouldLoadAdminApp()
        ? import("./AdminApp.tsx")
        : import("./App.tsx");

    void appModulePromise.then((module) => {
      if (!active) return;
      setAppComponent(() => module.default);
    });

    void shellReadyPromise.then((shouldRender) => {
      if (!active || shouldRender) return;
      setAppComponent(() => () => null);
      setMinimumBootElapsed(true);
      setShellReady(false);
    });

    return () => {
      active = false;
      if (bootTimerId !== null) {
        window.clearTimeout(bootTimerId);
        bootTimerId = null;
      }
    };
  }, []);

  return (
    <AppRevealProvider
      shellReady={shellReady}
      appModuleReady={AppComponent !== null}
      minimumBootElapsed={minimumBootElapsed}
    >
      {AppComponent ? <AppComponent /> : null}
    </AppRevealProvider>
  );
}

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <BootstrapHost />
    </React.StrictMode>,
  );
}
